// Reine Rechnung des Feinstaub-Piloten auf der Startseite (docs/protokoll.md,
// „Browser (Phase 4)"). Ohne Svelte und ohne Wert-Importe, damit node sie testen
// kann (lib/protokoll-logik.test.mjs); feinstaub.svelte.ts hält nur die Leitung.

/** Das, was die Rechnung vom Schema braucht (Teil von `FeldInfo`). */
export interface FeldSkala {
  readonly name: string;
  readonly skala?: number;
}

/** Entität (Teil von `DeviceEntity`), soweit die Rechnung sie braucht. */
export interface KartenEntitaet {
  slug: string;
  state: string;
}

/** Ein Stand gilt als lebendig, solange die letzte Meldung nicht älter ist als
 *  2 × `lebenszeichen_ms` des Topics (120 s bei `feinstaub.luft`). */
export const VERFALL_FAKTOR = 2;

/** Instanzname zu einem ESPHome-Gerät: `haDeviceId` = `feinstaub:<node>` (die
 *  Kategorie des Ingesters plus die MQTT-ID unter der Wurzel `feinstaub/`) →
 *  `feinstaub.<node>.luft`. Alles andere (andere Kategorie, ID mit Zeichen, die
 *  das Protokoll als Segment nicht kennt) → undefined. */
export function feinstaubInstanz(haDeviceId: string): string | undefined {
  const m = /^feinstaub:([A-Za-z0-9_-]+)$/.exec(haDeviceId);
  return m ? `feinstaub.${m[1]}.luft` : undefined;
}

/** Slug der Entität zu einem Protokollfeld: gleicher Name, nur `pm10_0` heißt
 *  in der MQTT-Welt `pm10`. */
export function slugZuFeld(feld: string): string {
  return feld === "pm10_0" ? "pm10" : feld;
}

/** Nachkommastellen, die eine Skala trägt: 0,1 → 1, 0,001 → 3, ab 1 → 0. */
export function stellenDerSkala(skala: number): number {
  if (!(skala > 0) || skala >= 1) return 0;
  return Math.min(9, Math.ceil(-Math.log10(skala) - 1e-9));
}

/** Drahtzahl → Zustandstext wie der MQTT-Ingester ihn liefert
 *  (`strconv.FormatFloat(v, 'f', -1, 64)`: ohne Nullen am Ende, Punkt als
 *  Dezimaltrenner). Wert = Drahtzahl × Skala; auf die Stellen der Skala
 *  gerundet, damit 123 × 0,1 nicht als 12.3000000000000007 endet. */
export function zustandsText(draht: number, skala: number | undefined): string {
  const s = skala && skala > 0 ? skala : 1;
  const wert = Number((draht * s).toFixed(stellenDerSkala(s)));
  return String(Object.is(wert, -0) ? 0 : wert);
}

/** Protokollwerte eines Geräts als Zustandstexte je Entitäts-Slug.
 *  Leer, wenn kein Stand da ist oder er verfallen ist (`jetztMs` und `zMs`
 *  stammen aus der Uhr des Dienstes); Felder, die fehlen, `null` oder keine
 *  Zahl sind, bleiben weg. Dann gilt für sie der bisherige Wert. */
export function protokollZustaende(
  stand: Readonly<Record<string, unknown>> | undefined,
  zMs: number | undefined,
  jetztMs: number,
  felder: readonly FeldSkala[],
  lebenszeichenMs: number,
): Record<string, string> {
  const aus: Record<string, string> = {};
  if (!stand) return aus;
  if (lebenszeichenMs > 0 && zMs !== undefined && jetztMs - zMs > VERFALL_FAKTOR * lebenszeichenMs) return aus;
  for (const f of felder) {
    const v = stand[f.name];
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    aus[slugZuFeld(f.name)] = zustandsText(v, f.skala);
  }
  return aus;
}

/** Entitäten mit Protokollwerten statt des MQTT-Zustands. Es kommen keine
 *  Entitäten hinzu und keine fallen weg; ohne Treffer bleibt die Liste (dasselbe
 *  Objekt) unverändert. */
export function mitProtokollwerten<E extends KartenEntitaet>(entitaeten: E[], zustaende: Readonly<Record<string, string>>): E[] {
  if (!entitaeten.some((e) => e.slug in zustaende)) return entitaeten;
  return entitaeten.map((e) => (e.slug in zustaende ? { ...e, state: zustaende[e.slug] } : e));
}
